<template>
    <div class="QuizSessionView">
        <StepBackground :image="course.bannerImage" />

        <ProgressBar :progress="progress" />

        <div class="header">
            <Lives v-if="remainingLives > -1" :remaining="remainingLives" />
            <div class="spacer" />

            <div class="spacer" />
            <div class="backButton --global-clickable" @click="exit">
                <svgicon icon="close" />
            </div>
        </div>

        <QuizHost v-if="session" :session="session" />
    </div>
</template>

<script>
import { mapState } from 'vuex';

import store from '@/store';
import ThemeMixin from '@/modules/course/theme-mixin';
import ProgressBar from '@/components/ProgressBar';
import QuizHost from '@/modules/quiz/components/QuizHost';
import Lives from '@/modules/games-shared/components/Lives';

import QuizSession from '@/modules/quiz/quiz-session';

import StepBackground from '@/modules/course/components/StepBackground';

import quizHelper from '@/modules/course/quiz-helper';

export default {
    components: { StepBackground, ProgressBar, QuizHost, Lives },

    mixins: [ThemeMixin],

    data() {
        return {
            loading: true,
            remainingLives: -1,
            progress: 0,
            session: null,
            timeUsed: 0
        };
    },

    computed: {
        ...mapState({
            games: state => state.moduleMemoransier.games
        }),

        gameId() {
            return this.$route.params.gameId;
        },

        game() {
            return this.games.find(game => game.id === this.gameId);
        },

        gameOptions() {
            return JSON.parse(this.game.options);
        },

        stepGroup() {
            return this.$store.state.moduleCourse.stepGroups[this.gameOptions.stepGroupId];
        },

        course() {
            return this.$store.state.moduleCourse.courses[this.gameOptions.courseId];
        }
    },

    async mounted() {
        this.loading = false;

        const quizState = this.$store.getters['moduleCourse/getStepGroupQuizState'](this.stepGroup.id);

        const dataProvider = await quizHelper.createQuestionProvider(this.stepGroup.quiz, {
            stepGroupId: this.stepGroup.id,
            quizState
        });

        this.session = new QuizSession(dataProvider, { lives: 3 });
        this.session.on('update', this.onUpdate);
        this.session.on('end', this.onEnd);
        this.onUpdate();

        this.refreshingIntervalId = setInterval(async () => {
            await this.refreshGame();
            const isGameFinished = this.$store.state.moduleMemoransier.games.find(g => g.id === this.gameId)
                ?.leaderboardReveal;

            if (isGameFinished) {
                clearInterval(this.refreshingIntervalId);
                this.goToPodiumView();
            }
        }, 1000);

        this.timeIntervalId = setInterval(() => {
            this.timeUsed += 1;
        }, 1000);
    },

    beforeDestroy() {
        clearInterval(this.countdownId);
    },

    methods: {
        onUpdate() {
            this.progress = this.session.correctCount / this.session.totalCount;
            this.remainingLives = this.session.lives;

            const completed = this.session.correctCount === this.session.totalCount;

            this.$store.dispatch('moduleMemoransier/updatePlayer', {
                gameId: this.gameId,
                correct: this.session.correctCount,
                completed,
                time: { seconds: this.timeUsed }
            });
        },
        onEnd() {
            this.$store.dispatch('moduleCourse/registerStepGroupQuizResult', {
                stepGroupId: this.stepGroupId,
                summary: this.session.getSummary()
            });

            this.exit();
        },
        exit() {
            this.goToPodiumView();
        },
        async refreshGame() {
            await this.$store.dispatch('moduleMemoransier/refresh');
        },
        goToPodiumView() {
            this.$router.push({
                name: 'MemoransierPodiumView',
                props: {
                    gameId: this.gameId
                }
            });
        }
    },

    async beforeRouteEnter(to, from, next) {
        await store.dispatch('moduleCourse/loadCourseFull', { courseId: to.params.courseId });

        next();
    }
};
</script>

<style lang="scss" scoped>
.QuizSessionView {
    display: flex;
    flex-direction: column;
}

.header {
    position: relative;
    color: rgba(white, 0.9);
    height: $topBarHeight;
    display: flex;
    align-items: center;
}
.backButton {
    padding: 0 1em;
    height: $topBarHeight;
    display: flex;
    align-items: center;
    position: absolute;
    top: 0;
    right: 0;
    .svg-icon {
        $size: 0.8em;
        width: $size;
        height: $size;
        fill: rgba(white, 0.9);
    }
}
.spacer {
    flex: 1;
}

.Lives {
    margin-left: 2em;
}

.countdown {
    margin-left: 1em;
}

.QuizHost {
    flex: 1;
}

@include media('>=mobile') {
    .backButton {
        right: 1em;
    }
}
@include media('<mobile') {
    .Lives {
        margin-left: 1em;
    }
}
</style>
